import { useState } from "react";
import { api } from "../api";
import { getTheme, setTheme } from "../theme";
import type { Me } from "../types";
import { ProfileEditor, type ProfileEditorTab } from "./ProfileEditor";
import { type ExtraPage, ProfileExtras } from "./ProfileExtras";
import { OwnProfilePreview } from "./ProfilePreview";
import { MenuRow, PageDialog } from "./ReferenceUI";

export function Profile({
  me,
  onRefresh,
  onDeleted,
  onOpenNotifications,
  onOpenNotificationSettings,
  onOpenFilters,
  onOpenBlockedUsers,
}: {
  me: Me;
  onRefresh(): Promise<void>;
  onDeleted(): void;
  onOpenNotifications(): void;
  onOpenNotificationSettings(): void;
  onOpenFilters(): void;
  onOpenBlockedUsers(): void;
}) {
  const [editorStart, setEditorStart] = useState<ProfileEditorTab>("info");
  const openEditor = (tab: ProfileEditorTab = "info") => {
    setEditorStart(tab);
    setPage("edit");
  };
  const [theme, updateTheme] = useState(getTheme);
  const [page, setPage] = useState<ExtraPage | "edit" | "preview" | "account" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const p = me.profile;
  if (!p) return null;
  const primary = p.photos.find((photo) => photo.isPrimary) ?? p.photos[0];
  const age = (() => {
    const birthday = new Date(p.birthDate);
    const now = new Date();
    return (
      now.getUTCFullYear() -
      birthday.getUTCFullYear() -
      (now.getUTCMonth() < birthday.getUTCMonth() ||
      (now.getUTCMonth() === birthday.getUTCMonth() && now.getUTCDate() < birthday.getUTCDate())
        ? 1
        : 0)
    );
  })();
  const visibility = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/v1/me/visibility", { method: "PUT", body: JSON.stringify({ isDiscoverable: !p.isDiscoverable }) });
      await onRefresh();
    } catch {
      setError("Не удалось изменить видимость анкеты.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/v1/me/account", { method: "DELETE", body: JSON.stringify({ confirmation: "delete_account" }) });
      onDeleted();
    } catch {
      setError("Не удалось удалить аккаунт. Попробуйте ещё раз.");
      setBusy(false);
    }
  };
  return (
    <section className="content-page reference-profile">
      <button className="reference-preview-link" type="button" onClick={() => setPage("preview")}>
        См. профиль
      </button>
      <div className="reference-identity">
        <button
          type="button"
          className="reference-avatar"
          aria-label="Изменить фотографии"
          onClick={() => openEditor("photos")}
        >
          {primary ? <img src={primary.url} alt="Моя фотография" /> : <span>Фото</span>}
          <span>✎</span>
        </button>
        <h1>
          {p.displayName}, {age}
        </h1>
        {p.moderationStatus !== "active" && (
          <p className="reference-note">
            {primary?.moderationStatus === "rejected" ? "Фото отклонено. Загрузите другое." : "Анкета на проверке"}
          </p>
        )}
      </div>
      <button type="button" className="reference-premium-entry" onClick={() => setPage("premium")}>
        <span aria-hidden="true">◇</span>
        <span>
          <strong>DonDate Premium</strong>
          <small>Возможности подписки</small>
        </span>
        <span>›</span>
      </button>
      <div className="reference-group">
        <MenuRow icon="✎" label="Изменить профиль" onClick={() => openEditor()} />
        <MenuRow icon="▷" label="Моё видео" onClick={() => openEditor("video")} />
        <MenuRow icon="♧" label="Пригласить" onClick={() => setPage("invite")} />
        <MenuRow icon="♧" label="Уведомления" onClick={onOpenNotificationSettings} />
        <MenuRow icon="◐" label="Тёмное оформление">
          <input
            aria-label="Тёмное оформление"
            type="checkbox"
            className="reference-switch"
            checked={theme === "dark"}
            onChange={(e) => {
              const next = e.target.checked ? "dark" : "light";
              setTheme(next);
              updateTheme(next);
            }}
          />
        </MenuRow>
      </div>
      <div className="reference-group">
        <MenuRow icon="ⓘ" label="О нас" onClick={() => setPage("about")} />
        <MenuRow icon="▤" label="Соглашения" onClick={() => setPage("legal")} />
        <MenuRow icon="?" label="Помощь" onClick={() => setPage("help")} />
      </div>
      <div className="reference-group">
        <MenuRow icon="⚙" label="Настройки аккаунта" onClick={() => setPage("account")} />
      </div>
      {!p.isDiscoverable && <p className="reference-note">Анкета скрыта. Ваши переписки сохранены.</p>}
      {error && !page && (
        <p className="reference-error" role="alert">
          {error}
        </p>
      )}
      {page === "account" && (
        <PageDialog title="Настройки аккаунта" onClose={() => setPage(null)} busy={busy}>
          <div className="reference-group">
            <MenuRow
              label="Настройки поиска"
              onClick={() => {
                onOpenFilters();
              }}
            />
            <MenuRow
              label="Центр уведомлений"
              onClick={() => {
                onOpenNotifications();
              }}
            />
            <MenuRow
              label="Заблокированные"
              onClick={() => {
                onOpenBlockedUsers();
              }}
            />
            <MenuRow label="Показывать анкету">
              <input
                aria-label="Показывать мою анкету"
                className="reference-switch"
                type="checkbox"
                checked={p.isDiscoverable}
                disabled={busy}
                onChange={() => void visibility()}
              />
            </MenuRow>
            {me.adminRole && <MenuRow label="Панель модерации" onClick={() => location.assign("/admin")} />}
            <MenuRow
              label="Удалить аккаунт"
              onClick={() => {
                setError("");
                setPage("delete");
              }}
            />
          </div>
          <p className="reference-note">
            {p.isDiscoverable
              ? "Ваша анкета видна в поиске. Её можно скрыть, сохранив совпадения и переписки."
              : "Анкета скрыта из поиска. Совпадения и переписки сохранены."}
          </p>
          {error && (
            <p className="reference-error" role="alert">
              {error}
            </p>
          )}
        </PageDialog>
      )}
      {page === "edit" && (
        <ProfileEditor initialTab={editorStart} me={me} onRefresh={onRefresh} onClose={() => setPage(null)} />
      )}
      {page === "preview" && <OwnProfilePreview me={me} onClose={() => setPage(null)} onEdit={() => openEditor()} />}
      {page === "delete" && (
        <PageDialog title="Удалить аккаунт?" onClose={() => setPage("account")} busy={busy}>
          <p>Профиль, фотографии, совпадения и переписки будут удалены. Это действие нельзя отменить.</p>
          {error && (
            <p className="reference-error" role="alert">
              {error}
            </p>
          )}
          <button type="button" className="reference-primary" disabled={busy} onClick={() => void remove()}>
            {busy ? "Удаляем…" : "Удалить навсегда"}
          </button>
          <button type="button" className="reference-row" disabled={busy} onClick={() => setPage("account")}>
            Оставить аккаунт
          </button>
        </PageDialog>
      )}
      {page && ["premium", "invite", "about", "legal", "help"].includes(page) && (
        <ProfileExtras page={page as ExtraPage} onClose={() => setPage(null)} />
      )}
    </section>
  );
}
